<script setup lang="ts">
import { ref, computed } from "vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import { Facade } from "@/drawing";

defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "created"): void;
}>();

type FormatKey = "A4" | "A3" | "A2" | "A1" | "A0";

const FORMAT_SIZES: Record<FormatKey, { w: number; h: number }> = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  A2: { w: 420, h: 594 },
  A1: { w: 594, h: 841 },
  A0: { w: 841, h: 1189 },
};

const formatKeys = Object.keys(FORMAT_SIZES) as FormatKey[];

const name = ref("Plan 1");
const format = ref<FormatKey>("A4");
const landscape = ref(false);

const paperW = computed(() => {
  const f = FORMAT_SIZES[format.value];
  return landscape.value ? f.h : f.w;
});
const paperH = computed(() => {
  const f = FORMAT_SIZES[format.value];
  return landscape.value ? f.w : f.h;
});

const confirm = () => {
  const n = name.value.trim();
  if (!n) return;
  Facade.layouts.create(n, paperW.value, paperH.value);
  emit("created");
  emit("update:modelValue", false);
};

const close = () => {
  emit("update:modelValue", false);
};
</script>

<template>
  <DRDialog
    :model-value="modelValue"
    :title="$t('canvas:settings.paperSpace.dialog.title')"
    :confirm-label="$t('canvas:settings.paperSpace.dialog.confirm')"
    :width="420"
    @update:model-value="close"
    @cancel="close"
    @confirm="confirm"
  >
    <div class="d-flex flex-column gap-3">
      <div>
        <label class="form-label small mb-1">
          {{ $t("canvas:settings.paperSpace.dialog.name") }}
        </label>
        <input
          v-model="name"
          class="form-control form-control-sm"
          :placeholder="$t('canvas:settings.paperSpace.dialog.namePlaceholder')"
          @keyup.enter="confirm"
        />
      </div>

      <div>
        <label class="form-label small mb-2 d-block">
          {{ $t("canvas:settings.paperSpace.dialog.format") }}
        </label>
        <div class="d-flex gap-2">
          <button
            v-for="f in formatKeys"
            :key="f"
            type="button"
            class="btn btn-sm"
            :class="format === f ? 'btn-primary' : 'btn-outline-secondary'"
            @click="format = f"
          >
            {{ f }}
          </button>
        </div>
      </div>

      <div>
        <label class="form-label small mb-2 d-block">
          {{ $t("canvas:settings.paperSpace.dialog.orientation") }}
        </label>
        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-sm"
            :class="!landscape ? 'btn-primary' : 'btn-outline-secondary'"
            @click="landscape = false"
          >
            {{ $t("canvas:settings.paperSpace.dialog.portrait") }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="landscape ? 'btn-primary' : 'btn-outline-secondary'"
            @click="landscape = true"
          >
            {{ $t("canvas:settings.paperSpace.dialog.landscape") }}
          </button>
        </div>
      </div>

      <div class="text-muted small">{{ paperW }} × {{ paperH }} mm</div>
    </div>
  </DRDialog>
</template>
